import React, { useState } from "react";
import HealthieIntakeModal from "./HealthieIntakeModal";
import { useAuth } from "../context/AuthContext";

/**
 * TopNav — Integrated Wellness sibling-product navigation.
 *
 * Layout (desktop):
 *   [shield · Integrated Wellness / SELF-REGULATION CONSOLE]
 *   [DOMAINS · CALMING · RHYTHM · GROUNDING] [MAIN SITE ↗] [GET STARTED ↗]
 *
 * Hidden during live sessions (Mode A / Mode B) — that decision lives in App.js.
 */
const IW_SHIELD =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/amgkh1mp_logo.png";

// Medical care — licensed Integrated Medical team
const MEDICAL_URL = "https://integratednycai.com/medical-care";

const SECTIONS = [
  { id: "toolkit", label: "Toolkit" },
  { id: "how-it-works", label: "How it works" },
  { id: "checkins", label: "Check-ins" },
  { id: "faq", label: "FAQ" },
];

const scrollToId = (id, setOpen) => (e) => {
  e.preventDefault();
  if (setOpen) setOpen(false);
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.location.href = `/#${id}`;
  }
};


export default function TopNav() {
  const [open, setOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const { isMember, principal, openLogin, logout } = useAuth();

  const openIntake = (setMenu) => (e) => {
    e.preventDefault();
    if (setMenu) setMenu(false);
    setIntakeOpen(true);
  };

  const handleSignIn = (setMenu) => (e) => {
    e.preventDefault();
    if (setMenu) setMenu(false);
    openLogin(null);
  };

  const intakeSignIn = () => {
    setIntakeOpen(false);
    openLogin(null);
  };

  return (
    <nav
      data-testid="top-nav"
      className="sticky top-0 z-40 w-full"
      style={{
        background: "rgba(248, 250, 252, 0.92)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid var(--iw-border)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 xl:px-10 h-16 sm:h-20 flex items-center justify-between gap-4">
        {/* Brand — shield + wordmark */}
        <a
          href="/"
          className="flex items-center gap-3 flex-shrink-0 group"
          aria-label="Nervous System Reset · Integrated Wellness"
          data-testid="top-nav-home-link"
        >
          <img
            src={IW_SHIELD}
            alt="Integrated Wellness"
            width={44}
            height={44}
            className="flex-shrink-0"
            style={{ filter: "drop-shadow(0 3px 8px rgba(17, 41, 107, 0.22))" }}
          />
          <div className="flex flex-col leading-tight">
            <span
              className="serif text-[15px] sm:text-[18px] font-bold"
              style={{ color: "var(--iw-primary)", letterSpacing: "-0.005em" }}
            >
              Integrated Wellness
            </span>
            <span
              className="iw-mono text-[9px] sm:text-[10px] tracking-[0.22em] uppercase"
              style={{ color: "var(--iw-accent-red)", fontWeight: 600 }}
            >
              Nervous System Reset
            </span>
          </div>
        </a>

        {/* Center section links — monospace caps, evenly spaced */}
        <div
          className="hidden lg:flex items-center justify-center gap-1 flex-1 px-4"
          data-testid="top-nav-sections"
        >
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={scrollToId(s.id, null)}
              className="px-3 lg:px-4 py-2 transition-colors iw-mono uppercase whitespace-nowrap"
              style={{
                color: "var(--iw-primary)",
                fontSize: "12px",
                letterSpacing: "0.16em",
                fontWeight: 600,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--iw-accent-red)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--iw-primary)")}
              data-testid={`top-nav-section-${s.id}`}
            >
              {s.label}
            </a>
          ))}
        </div>

        {/* Right side — Peak State + Programs (wellness) + Medical (medical) + GET STARTED + mobile menu */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <a
            href="#peak-state"
            className="hidden xl:inline-flex items-center gap-1.5 iw-mono uppercase transition-colors whitespace-nowrap"
            style={{
              color: "var(--iw-primary)",
              fontSize: "11px",
              letterSpacing: "0.16em",
              fontWeight: 600,
              padding: "8px 0",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--iw-accent-red)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--iw-primary)")}
            data-testid="top-nav-peak-state-link"
            title="Peak State — guided focus & mental rehearsal (wellness · non-medical)"
          >
            Peak State ↓
          </a>

          <a
            href={MEDICAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden xl:inline-flex items-center gap-1.5 iw-mono uppercase transition-colors whitespace-nowrap"
            style={{
              color: "var(--iw-blue)",
              fontSize: "11px",
              letterSpacing: "0.16em",
              fontWeight: 600,
              padding: "8px 0",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--iw-accent-red)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--iw-blue)")}
            data-testid="top-nav-medical-link"
            title="Integrated Medical — licensed medical care (separate from this wellness tool)"
          >
            Medical Care <span aria-hidden="true">↗</span>
          </a>

          <a
            href="#begin"
            onClick={openIntake(setOpen)}
            className="iw-btn-primary"
            style={{ padding: "0.65rem 1.1rem", fontSize: "12px" }}
            data-testid="top-nav-get-started-btn"
          >
            <span className="hidden sm:inline">Get Started</span>
            <span className="sm:hidden">Start</span>
            <span aria-hidden="true">↗</span>
          </a>

          {isMember && (
            <a
              href="/dashboard"
              className="hidden sm:inline-flex items-center iw-mono uppercase transition-colors whitespace-nowrap"
              style={{ color: "var(--iw-primary)", fontSize: "11px", letterSpacing: "0.16em", fontWeight: 600, padding: "8px 0" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--iw-accent-red)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--iw-primary)")}
              data-testid="top-nav-dashboard-link"
            >
              My Progress
            </a>
          )}
          {isMember ? (
            <button
              type="button"
              onClick={() => logout()}
              className="hidden sm:inline-flex items-center iw-mono uppercase transition-colors whitespace-nowrap"
              style={{ color: "var(--iw-primary)", fontSize: "11px", letterSpacing: "0.16em", fontWeight: 600, padding: "8px 0" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--iw-accent-red)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--iw-primary)")}
              title={principal?.name ? `Signed in as ${principal.name}` : "Sign out"}
              data-testid="top-nav-signout-btn"
            >
              Sign out
            </button>
          ) : (
            <a
              href="#signin"
              onClick={handleSignIn(setOpen)}
              className="hidden sm:inline-flex items-center iw-mono uppercase transition-colors whitespace-nowrap"
              style={{ color: "var(--iw-primary)", fontSize: "11px", letterSpacing: "0.16em", fontWeight: 600, padding: "8px 0" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--iw-accent-red)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--iw-primary)")}
              data-testid="top-nav-signin-btn"
            >
              Sign in
            </a>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 transition-all"
            style={{
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
              color: "var(--iw-primary)",
              background: open ? "var(--iw-blue-tint)" : "transparent",
            }}
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            data-testid="top-nav-mobile-toggle"
          >
            <span aria-hidden="true" className="font-mono text-base leading-none">
              {open ? "✕" : "☰"}
            </span>
          </button>
        </div>
      </div>

      {/* Mobile drop-down panel */}
      {open && (
        <div
          className="lg:hidden border-t"
          style={{
            background: "var(--iw-surface)",
            borderColor: "var(--iw-border)",
          }}
          data-testid="top-nav-mobile-panel"
        >
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={scrollToId(s.id, setOpen)}
                className="px-3 py-3 iw-mono uppercase border-b last:border-b-0"
                style={{
                  color: "var(--iw-primary)",
                  fontSize: "13px",
                  letterSpacing: "0.16em",
                  fontWeight: 600,
                  borderColor: "var(--iw-border-soft)",
                }}
                data-testid={`top-nav-mobile-section-${s.id}`}
              >
                {s.label}
              </a>
            ))}
            <a
              href="#peak-state"
              className="px-3 py-3 iw-mono uppercase border-b"
              style={{
                color: "var(--iw-primary)",
                fontSize: "13px",
                letterSpacing: "0.16em",
                fontWeight: 600,
                borderColor: "var(--iw-border-soft)",
              }}
              data-testid="top-nav-mobile-peak-state"
            >
              Peak State <span style={{ color: "var(--iw-muted)", fontSize: "10px", marginLeft: 6 }}>· Wellness</span>
            </a>
            <a
              href={MEDICAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-3 iw-mono uppercase border-b"
              style={{
                color: "var(--iw-blue)",
                fontSize: "13px",
                letterSpacing: "0.16em",
                fontWeight: 600,
                borderColor: "var(--iw-border-soft)",
              }}
              data-testid="top-nav-mobile-medical"
            >
              Medical Care ↗ <span style={{ color: "var(--iw-muted)", fontSize: "10px", marginLeft: 6 }}>· Licensed</span>
            </a>
            <a
              href="#begin"
              onClick={openIntake(setOpen)}
              className="px-3 py-3 iw-mono uppercase"
              style={{
                color: "var(--iw-accent-red)",
                fontSize: "13px",
                letterSpacing: "0.16em",
                fontWeight: 700,
              }}
              data-testid="top-nav-mobile-get-started"
            >
              Get Started ↗
            </a>
            {isMember && (
              <a
                href="/dashboard"
                className="px-3 py-3 iw-mono uppercase"
                style={{ color: "var(--iw-primary)", fontSize: "13px", letterSpacing: "0.16em", fontWeight: 600 }}
                data-testid="top-nav-mobile-dashboard"
              >
                My Progress
              </a>
            )}
            {isMember ? (
              <button
                type="button"
                onClick={() => { setOpen(false); logout(); }}
                className="px-3 py-3 iw-mono uppercase text-left"
                style={{ color: "var(--iw-primary)", fontSize: "13px", letterSpacing: "0.16em", fontWeight: 600 }}
                data-testid="top-nav-mobile-signout"
              >
                Sign out
              </button>
            ) : (
              <a
                href="#signin"
                onClick={handleSignIn(setOpen)}
                className="px-3 py-3 iw-mono uppercase"
                style={{ color: "var(--iw-primary)", fontSize: "13px", letterSpacing: "0.16em", fontWeight: 600 }}
                data-testid="top-nav-mobile-signin"
              >
                Sign in
              </a>
            )}
          </div>
        </div>
      )}

      <HealthieIntakeModal
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        onSignIn={intakeSignIn}
      />
    </nav>
  );
}
